import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Badge from "react-bootstrap/Badge";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import "./Prayers.scss";

const PrayersComponent = ({ isTV = false, bookmarks = [], toggleBookmark }) => {
  const { t } = useTranslation();

  // Estado
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Verificar si una oración está marcada
  const isPrayerBookmarked = (prayerId) => {
    return bookmarks.some(b => b.type === 'prayer' && b.itemId === prayerId);
  };

  // Manejar toggle de marcador
  const handleBookmarkToggle = (e, prayerId, prayerTitle) => {
    e.stopPropagation(); // Evitar que se abra la oración al hacer clic en el marcador
    if (toggleBookmark) {
      toggleBookmark({
        type: 'prayer',
        itemId: prayerId,
        title: prayerTitle
      });
    }
  };

  // Categorías de oraciones
  const prayerCategories = [
    {
      id: "confession",
      title: "prayers_enhanced.categories.confession",
      icon: "🕊️",
      color: "confession",
      prayers: [
        { id: "prayer_before_confession", title: "prayers.prayer_before_confession" },
        { id: "act_of_contrition", title: "prayers.act_of_contrition" },
        { id: "another_act_of_contrition", title: "prayers.another_act_of_contrition" },
        { id: "thanksgiving_after_confession", title: "prayers.thanksgiving_after_confession" },
      ],
    },
    {
      id: "common",
      title: "prayers_enhanced.categories.common",
      icon: "🙏",
      color: "common",
      prayers: [
        { id: "sign_of_cross", title: "prayers.sign_of_cross_prayer" },
        { id: "apostles_creed", title: "prayers.apostles_creed" },
        { id: "confiteor", title: "prayers.confiteor" },
        { id: "our_father", title: "prayers.our_father" },
        { id: "hail_mary", title: "prayers.hail_mary" },
        { id: "hail_holy_queen", title: "prayers.hail_holy_queen" },
      ],
    },
  ];

  // Handlers
  const handlePrayerSelect = (prayerId) => {
    setSelectedPrayer(prayerId);
    setSearchTerm("");
  };

  const handleBack = () => {
    setSelectedPrayer(null);
  };

  const filteredCategories = prayerCategories.map((category) => ({
    ...category,
    prayers: category.prayers.filter((prayer) =>
      t(prayer.title).toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter((category) => category.prayers.length > 0);

  // Vista de oración individual
  if (selectedPrayer) {
    const category = prayerCategories.find((cat) =>
      cat.prayers.some((p) => p.id === selectedPrayer)
    );
    const prayer = category?.prayers.find((p) => p.id === selectedPrayer);

    return (
      <div className="prayers-simple prayer-view">
        <Container className="rosario-container">
          <div className="prayer-content-wrapper">
            {/* Header */}
            <div className="prayer-header mb-4">
              <Button variant="link" className="text-muted p-0" onClick={handleBack}>
                ← {t("prayers_enhanced.back_to_list")}
              </Button>
            </div>

            {/* Tarjeta de oración */}
            <Card className="prayer-card-main" onClick={handleBack}>
              <Card.Body className="prayer-card-body text-center">
                <div className="prayer-content-full">
                  {/* Badge de categoría */}
                  <div className={`prayer-category-badge category-${category?.color || "common"} mb-3`}>
                    {category?.icon} {t(category?.title)}
                  </div>

                  {/* Título con botón de marcador */}
                  <div className="prayer-title-with-bookmark mb-4">
                    <h2 className="prayer-title-main mb-0">
                      {t(prayer?.title)}
                    </h2>
                    <Button
                      variant="link"
                      className={`bookmark-toggle-btn ${isPrayerBookmarked(selectedPrayer) ? 'bookmarked' : ''}`}
                      onClick={(e) => handleBookmarkToggle(e, selectedPrayer, t(prayer?.title))}
                      aria-label={isPrayerBookmarked(selectedPrayer) ? t('bookmarks.remove') : t('bookmarks.add')}
                    >
                      <i className={`fa ${isPrayerBookmarked(selectedPrayer) ? 'fa-bookmark' : 'fa-bookmark-o'}`}></i>
                    </Button>
                  </div>

                  {/* Texto de la oración */}
                  <div className="prayer-text-full">
                    {selectedPrayer === "act_of_contrition" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.act_of_contrition_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "another_act_of_contrition" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.another_act_of_contrition_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "prayer_before_confession" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.prayer_before_confession_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "thanksgiving_after_confession" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.thanksgiving_after_confession_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "our_father" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.our_father_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "hail_mary" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.hail_mary_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "hail_holy_queen" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.hail_holy_queen_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "sign_of_cross" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.sign_of_cross_prayer_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "apostles_creed" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.apostles_creed_text" />
                        </p>
                      </div>
                    )}
                    {selectedPrayer === "confiteor" && (
                      <div className="trans-container">
                        <p className="prayer-paragraph">
                          <Trans i18nKey="prayers.confiteor_text" />
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Indicador */}
                  <div className="tap-indicator mt-4 text-muted">
                    <small>{t("prayers_enhanced.tap_back")}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  // Vista de lista de oraciones
  return (
    <div className="prayers-simple">
      <Container className="rosario-container py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8} xl={7}>
            {/* Header */}
            <div className="prayers-header mb-4">
              <h1 className="text-center mb-3">{t("prayers.prayers")}</h1>
              <p className="text-center text-muted mb-4">
                {t("prayers_enhanced.subtitle")}
              </p>

              {/* Búsqueda */}
              <Form.Control
                type="text"
                placeholder={t("prayers_enhanced.search_placeholder")}
                className="search-bar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Categorías con tarjetas */}
            {filteredCategories.map((category) => (
              <div key={category.id} className="prayer-category mb-4">
                <div className="category-title mb-3">
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-name">{t(category.title)}</span>
                  <Badge bg="secondary" className="category-count">
                    {category.prayers.length}
                  </Badge>
                </div>

                <div className="prayer-cards-grid">
                  {category.prayers.map((prayer) => (
                    <Card
                      key={prayer.id}
                      className={`prayer-select-card category-${category.color} ${isPrayerBookmarked(prayer.id) ? 'bookmarked' : ''}`}
                      onClick={() => handlePrayerSelect(prayer.id)}
                    >
                      <Button
                        variant="link"
                        className="card-bookmark-btn"
                        onClick={(e) => handleBookmarkToggle(e, prayer.id, t(prayer.title))}
                        aria-label={isPrayerBookmarked(prayer.id) ? t('bookmarks.remove') : t('bookmarks.add')}
                      >
                        <i className={`fa ${isPrayerBookmarked(prayer.id) ? 'fa-bookmark' : 'fa-bookmark-o'}`}></i>
                      </Button>
                      <Card.Body className="text-center">
                        <h5 className="prayer-card-title mb-0">
                          {t(prayer.title)}
                        </h5>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            ))}

            {/* Sin resultados */}
            {filteredCategories.every((cat) => cat.prayers.length === 0) && (
              <div className="text-center py-5">
                <div className="no-results-icon">🔍</div>
                <p className="text-muted mt-3">{t("prayers_enhanced.no_results")}</p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PrayersComponent;
